import LoanOriginationSystemApi from './LoanOriginationSystemApi';
import { BorrowerType } from './LoanOriginationSystemBorrowersApi';
import { UserInfo } from './LoanOriginationSystemApplicationsApi';
import formatDate from 'utils/dateFormat';

export enum ProductStatus {
  Archived = 'archived',
  Active = 'active',
  Draft = 'draft',
}

export enum ProductType {
  Custom = 'custom',
  CreditCard = 'creditCard',
  PersonalLoan = 'personalLoan',
  AutoLoanRefinancing = 'autoLoanRefinancing',
  Mortgage = 'mortgage',
  HomeEquityLoan = 'homeEquityLoan',
  HomeImprovementLoan = 'homeImprovementLoan',
  PointOfSaleFinancing = 'pointOfSaleFinancing',
  SmallBusinessLoan = 'smallBusinessLoan',
  CommercialLoan = 'commercialLoan',
}

export enum ProductApiRequestTemplateType {
  CreateApplication = 'createApplication',
  UpdateApplication = 'updateApplication',
  CreateTask = 'createTask',
  UpdateTask = 'updateTask',
}

export enum AssigneeTeamMembersType {
  OnCreate = 'onCreate',
  RoundRobin = 'roundRobin',
  SpecificTeamMembers = 'specificTeamMembers',
}

export interface ProductSettings {
  docuSignEnabled: boolean;
  autoAssignLabels: string[];
  applicationFormPages: string[];
  assigneeTeamMembersType: AssigneeTeamMembersType | null;
  teamMembersToAssign: string[];
  teamMembersToRoundRobin: string[];
  lockBorrowerOnApplicationCreation: boolean;
}

export interface Product {
  id: string;
  type: ProductType;
  name: string;
  status: ProductStatus;
  borrowerTypes: BorrowerType[];
  declineReasons: string[];
  statuses: string[];
  settings: ProductSettings;
  createdAt: Date;
  updatedAt: Date;
  createdBy: UserInfo | null;
  updatedBy: UserInfo | null;
}

export interface CreateProductParams {
  name: string;
  borrowerTypes: BorrowerType[];
  type: ProductType;
  status: ProductStatus;
}

export interface UpdateProductParams {
  status?: ProductStatus;
  name?: string;
  borrowerTypes?: BorrowerType[];
  type?: ProductType;
  settings?: Partial<ProductSettings>;
}

export interface ProductsDataFilter {
  search?: string;
  showArchived?: boolean;
  statuses?: ProductStatus[];
  borrowerType?: BorrowerType | null;
  selectedMembers?: UserInfo[];
  dueCreatedDateRange?: {
    from: Date | null;
    to: Date | null;
  };
  dueUpdatedDateRange?: {
    from: Date | null;
    to: Date | null;
  };
}

export interface LoanOriginationSystemProductsApi {
  getAll(filters?: ProductsDataFilter): Promise<Product[]>;
  getDropdownProducts(search: string, abortSignal: AbortSignal): Promise<Product[]>;
  getById(productId: string): Promise<Product>;
  update(id: string, product: UpdateProductParams): Promise<Product>;
  duplicate(id: string): Promise<Product>;
  create(params: CreateProductParams): Promise<Product>;
}

export default class LoanOriginationSystemProductsRestApi extends LoanOriginationSystemApi<Product>
  implements LoanOriginationSystemProductsApi {
  protected resourceName = 'products';

  public getAll(filters?: ProductsDataFilter) {
    const params = new URLSearchParams();

    if (filters) {
      const statuses = filters.statuses || [ProductStatus.Active, ProductStatus.Draft, ProductStatus.Archived];

      if (filters.selectedMembers) {
        filters.selectedMembers.forEach((member) => params.append('teamMemberIds', member.id));
      }

      if (filters.search) {
        params.set('search', filters.search);
      }

      if (filters.showArchived) {
        params.append('showArchived', filters.showArchived.toString());
      }

      statuses.forEach((status) => {
        params.append('statuses', status);
      });

      if (filters.borrowerType) {
        params.append('borrowerType', filters.borrowerType);
      }

      if (filters.dueCreatedDateRange && filters.dueCreatedDateRange.from) {
        params.append('dueCreatedDateRangeFrom', formatDate(filters.dueCreatedDateRange.from));
      }

      if (filters.dueCreatedDateRange && filters.dueCreatedDateRange.to) {
        params.append('dueCreatedDateRangeTo', formatDate(filters.dueCreatedDateRange.to));
      }

      if (filters.dueUpdatedDateRange && filters.dueUpdatedDateRange.from) {
        params.append('dueUpdatedDateRangeFrom', formatDate(filters.dueUpdatedDateRange.from));
      }

      if (filters.dueUpdatedDateRange && filters.dueUpdatedDateRange.to) {
        params.append('dueUpdatedDateRangeTo', formatDate(filters.dueUpdatedDateRange.to));
      }
    }

    return this.getResources(params);
  }

  public getDropdownProducts(search: string, abortSignal: AbortSignal) {
    const params = new URLSearchParams();

    params.append('statuses', ProductStatus.Active);
    params.append('search', search);

    return this.fetch<Product[]>(`/${this.resourceName}/?${params}`, 'GET', undefined, { abortSignal });
  }

  public getById(productId: string) {
    return this.getResourceById(productId);
  }

  public update(id: string, product: UpdateProductParams) {
    return this.updateResource(id, { product });
  }

  public duplicate(id: string) {
    return this.fetch<Product>(`/${this.resourceName}/${id}/duplicate`, 'POST');
  }

  public create(params: CreateProductParams) {
    return this.createResource({ product: params });
  }
}
